"use client";

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface Section {
  id: string;
  title: string;
  element: HTMLElement;
}

export function ReadingProgress() {
  const [sections, setSections] = useState<Section[]>([]);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    // Find all sections with headings
    const sectionElements =
      document.querySelectorAll<HTMLElement>("[data-section-id]");
    const sectionsData: Section[] = Array.from(sectionElements)
      .map((el) => {
        const heading =
          el.querySelector("h2")?.textContent ||
          el.querySelector("h3")?.textContent ||
          "";
        if (!heading) return null;

        return {
          id: el.dataset.sectionId || "",
          title: heading,
          element: el,
        };
      })
      .filter((s): s is Section => s !== null);

    setSections(sectionsData);

    // Intersection Observer to track active section
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = (entry.target as HTMLElement).dataset.sectionId;
            if (sectionId) {
              setActiveSection(sectionId);
            }
          }
        });
      },
      {
        rootMargin: "-20% 0px -60% 0px",
        threshold: 0,
      },
    );

    sectionElements.forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      section.element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  if (!mounted || sections.length === 0) return null;

  const progressIndicator = (
    <div className="hidden xl:block fixed top-40 right-0 z-50 max-w-60  px-5 py-3 bg-white">
      <nav className="space-y-3">
        {sections.map((section) => {
          const isActive = activeSection === section.id;
          return (
            <div
              key={section.id}
              className="flex items-center justify-end gap-4"
            >
              <motion.span
                initial={{ opacity: 0, x: 10 }}
                animate={{
                  opacity: isActive ? 1 : 0,
                  x: isActive ? 0 : 10,
                }}
                transition={{ duration: 0.2 }}
                className="text-sm text-gray-700 font-semibold text-right line-clamp-1 text-ellipsis truncate max-w-54"
              >
                {section.title}
              </motion.span>
              <button
                type="button"
                onClick={() => scrollToSection(section.id)}
                className="group relative cursor-pointer"
                aria-label={`Scroll to ${section.title}`}
              >
                <motion.div
                  className={`w-3 h-3 rounded-full border-2 transition-all ${
                    isActive
                      ? "border-brand-blue bg-brand-blue scale-125"
                      : "border-gray-400 bg-white hover:border-brand-blue"
                  }`}
                  animate={{
                    scale: isActive ? 1.25 : 1,
                  }}
                />
              </button>
            </div>
          );
        })}
      </nav>
    </div>
  );

  return createPortal(progressIndicator, document.body);
}
